// src/components/GenericMeetingTemplateEdit.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import {
  IMeetingEmailTemplate,
  meetingEmailTemplateSchema,
} from '../Interfaces/IMeetingEmailTemplate';
import { GenericMeetingEmailFormFields } from './GenericMeetingEmailFormFields';
import type { IDocument } from '../Interfaces/IDocument';
import { useTheme } from '@mui/material';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';

interface Props {
  open: boolean;
  template: IMeetingEmailTemplate;
  onSave: (data: IMeetingEmailTemplate) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export function GenericMeetingTemplateEdit({
  open,
  template,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const theme = useTheme();

  // primäres Formular-Objekt
  const [form, setForm] = useState<IMeetingEmailTemplate>(
    meetingEmailTemplateSchema.parse(template)
  );
  // Dokumente führen wir separat, weil zod das Array default([]) parst
  const [docs, setDocs] = useState<IDocument[]>(template.Documents ?? []);

  useEffect(() => {
    setForm(meetingEmailTemplateSchema.parse(template));
    setDocs(template.Documents ?? []);
  }, [template]);

  const onField = <K extends keyof IMeetingEmailTemplate>(
    key: K,
    value: IMeetingEmailTemplate[K]
  ) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  const handleAddDoc = (newDocs: IDocument[]) => {
    setDocs(d => [...d, ...newDocs]);
  };
  const handleRemoveDoc = (idx: number) => {
    setDocs(d => d.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    const isEdit = Boolean((template as any).idMeetingEmailTemplate > 0);

    if (isEdit) {
      const confirmed = await showConfirmDialogComponent(
        'Warnung',
        'Wenn Sie Ihre Änderungen speichern, bearbeiten Sie eine Vorlage. Ihre Änderungen werden künftig auf alle Termine/Vorgänge angewendet, die dieses Template verwenden. Bereits bestehende Termine/Vorgänge sind von den Änderungen nicht betroffen.',
        'Speichern',
        'Abbrechen',
        theme
      );

      if (!confirmed) {
        return;
      }
    }

    onSave({ ...form, Documents: docs });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='lg' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>{form.Title || 'Template bearbeiten'}</Box>
        {onDelete && (
          <IconButton size='small' color='error' onClick={onDelete}>
            <Delete />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        <Box mt={1} />
        <GenericMeetingEmailFormFields
          form={form}
          onField={onField}
          docs={docs}
          onAddDoc={handleAddDoc}
          onRemoveDoc={handleRemoveDoc}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant='contained' onClick={handleSave}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
